import { StripeApiKeyStatus } from '@client/graphql/types/graphql';
import UpdateStripeApiDialog from '@client/page/management/stripe/stripe-api-key/component/update-stripe-api-key-dialog.tsx';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useModalManagerContext } from '@packages/ui/modal/modal-manager-context.ts';
import TableHeaderColumn from '@packages/ui/table/table-header-column.tsx';
import TableRowColumn from '@packages/ui/table/table-row-cell.tsx';
import TableRow from '@packages/ui/table/table-row.tsx';
import { TableCellAlignment } from '@packages/ui/table/table-type.ts';
import Table from '@packages/ui/table/table.tsx';
import { useTranslate } from '@tolgee/react';
import classNames from 'classnames';
import { useEffect } from 'react';
import useResolveStripeAPIStatus from '../logic/use-resolve-stripe-api-status';

interface ApiKey {
  id: string;
  name: string;
  secretKey: string;
  publishableKey: string;
  webhookSecret?: string | null;
}

export default function StripeApiKeyTable(props: { apiKey: ApiKey[]; loading?: boolean }) {
  const { t } = useTranslate();
  const { apiKey, loading } = props;

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Table data={apiKey} build={(item) => <TableGroupRow key={item.id} apiKey={item} />}>
        <TableHeaderColumn alignment={TableCellAlignment.Left}>
          {t('stripe.apiKey.table.name', 'Name')}
        </TableHeaderColumn>
        <TableHeaderColumn></TableHeaderColumn>
      </Table>
    </>
  );
}

function TableGroupRow(props: { apiKey: ApiKey }) {
  const { apiKey } = props;
  const dialogManager = useModalManagerContext();
  const { data: status, refetch } = useResolveStripeAPIStatus(apiKey.id);

  const editApiKey = () => {
    dialogManager.showDialog((onClose) => <UpdateStripeApiDialog apiKey={apiKey} onClose={onClose} />);
  };

  useEffect(() => {
    void refetch();
  }, [props.apiKey, refetch]);

  return (
    <>
      <TableRow onClick={editApiKey} key={apiKey.id} className="hover:bg-gray-100">
        <TableRowColumn alignment={TableCellAlignment.Left}>
          <div className="flex items-center gap-2">
            <div
              className={classNames(
                'w-2 h-2 rounded-full',
                status?.resolveStripeAPIStatus === StripeApiKeyStatus.Active ? 'bg-green-500' : 'bg-gray-400'
              )}
            />
            <p className="w-full">{apiKey.name}</p>
          </div>
        </TableRowColumn>
        <TableRowColumn className="text-end">
          <PencilSquareIcon className="w-6 h-6 text-gray-500 hover:scale-110 inline" />
        </TableRowColumn>
      </TableRow>
    </>
  );
}
